import { useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

const STATUS_FARBE = {
  gueltig: THEME.colors.success,
  moegliche_ruecklastschrift: THEME.colors.warning,
  dublette: THEME.colors.textMuted,
  fehler: THEME.colors.danger,
};
const STATUS_LABEL = {
  gueltig: "Gültig",
  moegliche_ruecklastschrift: "Mögliche Rücklastschrift",
  dublette: "Bereits vorhanden",
  fehler: "Fehler",
};

export default function FinanzImportSeite({ benutzerId }) {
  const [datei, setDatei] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [importErgebnis, setImportErgebnis] = useState(null);
  const [verlauf, setVerlauf] = useState([]);

  const ladeVerlauf = useCallback(() => {
    finanzenApi.importListe(benutzerId, { seiten_groesse: 10 }).then(d => setVerlauf(d.eintraege)).catch(() => {});
  }, [benutzerId]);

  useEffect(() => { ladeVerlauf(); }, [ladeVerlauf]);

  const dateiWaehlen = (e) => {
    setDatei(e.target.files[0] || null);
    setVorschau(null);
    setImportErgebnis(null);
    setFehler("");
  };

  const vorschauLaden = async () => {
    if (!datei) return;
    setLaedt(true);
    setFehler("");
    try {
      setVorschau(await finanzenApi.importVorschau(benutzerId, datei));
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  };

  const importBestaetigen = async () => {
    if (!datei) return;
    setLaedt(true);
    setFehler("");
    try {
      const daten = await finanzenApi.importAusfuehren(benutzerId, datei);
      setImportErgebnis(daten);
      setVorschau(null);
      setDatei(null);
      ladeVerlauf();
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  };

  return (
    <div>
      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>CSV importieren</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
          Aktuell wird nur das Sparkassen-Exportformat unterstützt. Vor dem endgültigen Import wird immer eine Vorschau angezeigt.
        </p>
        <input type="file" accept=".csv" onChange={dateiWaehlen} style={{ color: THEME.colors.text, marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 12 }}>
          <button style={fs.btn} onClick={vorschauLaden} disabled={!datei || laedt}>{laedt ? "Lädt…" : "Vorschau anzeigen"}</button>
          {vorschau && <button style={{ ...fs.btn, background: THEME.colors.success }} onClick={importBestaetigen} disabled={laedt}>Import bestätigen</button>}
        </div>
        {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
      </div>

      {importErgebnis && (
        <div style={{ ...fs.karte, borderLeft: `4px solid ${THEME.colors.success}` }}>
          <p style={{ color: THEME.colors.text, margin: 0 }}>
            Import #{importErgebnis.import_id} abgeschlossen: {importErgebnis.importierte_zeilen} neue Buchungen,{" "}
            {importErgebnis.zusammenfassung.dubletten} Dubletten, {importErgebnis.zusammenfassung.fehler} Fehler,{" "}
            {importErgebnis.zusammenfassung.ruecklastschriften_erkannt} mögliche Rücklastschriften erkannt.
          </p>
        </div>
      )}

      {vorschau && (
        <div style={fs.karte}>
          <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Vorschau</h3>
          <p style={{ color: THEME.colors.textMuted }}>
            {vorschau.zusammenfassung.gefunden} Buchungen gelesen · {vorschau.zusammenfassung.neu} neu ·{" "}
            {vorschau.zusammenfassung.dubletten} bereits vorhanden · {vorschau.zusammenfassung.fehler} fehlerhaft ·{" "}
            {vorschau.zusammenfassung.ruecklastschriften_erkannt} Rücklastschriften erkannt
          </p>
          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={fs.th}>Zeile</th><th style={fs.th}>Datum</th><th style={fs.th}>Zahlungspartner</th>
                  <th style={{ ...fs.th, textAlign: "right" }}>Betrag</th><th style={fs.th}>Status</th><th style={fs.th}>Info</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.zeilen.slice(0, 50).map(z => (
                  <tr key={z.zeile}>
                    <td style={fs.td}>{z.zeile}</td>
                    <td style={fs.td}>{z.buchungsdatum || "–"}</td>
                    <td style={fs.td}>{z.zahlungspartner_name || "–"}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{z.betrag != null ? `${z.betrag.toFixed(2)} €` : "–"}</td>
                    <td style={{ ...fs.td, color: STATUS_FARBE[z.status] }}>{STATUS_LABEL[z.status] || z.status}</td>
                    <td style={fs.td}>{z.fehler || z.angewendete_regel_name || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Importverlauf</h3>
        <div style={fs.tabelle}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={fs.th}>Datum</th><th style={fs.th}>Datei</th><th style={fs.th}>Zeilen</th>
                <th style={fs.th}>Importiert</th><th style={fs.th}>Dubletten</th><th style={fs.th}>Fehler</th><th style={fs.th}>Rücklastschriften</th>
              </tr>
            </thead>
            <tbody>
              {verlauf.map(v => (
                <tr key={v.id}>
                  <td style={fs.td}>{v.importiert_am}</td>
                  <td style={fs.td}>{v.dateiname}</td>
                  <td style={fs.td}>{v.zeilen_gesamt}</td>
                  <td style={fs.td}>{v.zeilen_importiert}</td>
                  <td style={fs.td}>{v.zeilen_dubletten}</td>
                  <td style={fs.td}>{v.zeilen_fehler}</td>
                  <td style={fs.td}>{v.ruecklastschriften_gefunden}</td>
                </tr>
              ))}
              {verlauf.length === 0 && <tr><td style={fs.td} colSpan={7}>Noch keine Importe.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
